import React, { useEffect, useState, useCallback, useMemo } from "react";
import useAccess from "../../hooks/useAccess";
import axios from "axios";

import "../../styles/PermissionManager.css";

function PermissionManager() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  const [roles, setRoles] = useState([]);
  const [pages, setPages] = useState([]);
  const [resources, setResources] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedPage, setSelectedPage] = useState("");
  const [selectedResource, setSelectedResource] = useState("");
  const [localChanges, setLocalChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  const { hasPermission } = useAccess();

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    permissions: {
      edit: hasPermission("PermissionManagerPage", "Permissions", "edit"),
    },
  };

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  // -------------------- Load Data --------------------
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [roleRes, pageRes, permRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/dropdowns/emp-role`, { headers }),
        axios.get(`${BASE_URL}/api/dropdowns/pages`, { headers }),
        axios.get(`${BASE_URL}/api/permissions`, { headers }),
      ]);
      setRoles(roleRes.data);
      setPages(pageRes.data);
      setPermissions(permRes.data);
    } catch (err) {
      console.error("❌ Failed to load initial data:", err);
      alert("Failed to load permissions. Check API connection.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, headers]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load all resources when page changes
  useEffect(() => {
    if (!selectedPage) {
      setResources([]);
      return;
    }
    const fetchResources = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/dropdowns/resources?page_id=${selectedPage}`,
          { headers }
        );
        setResources(res.data);
      } catch (err) {
        console.error("❌ Failed to load resources:", err);
      }
    };
    fetchResources();
  }, [selectedPage, BASE_URL, headers]);

  // -------------------- Helpers --------------------

  const STANDARD_ACTIONS = ["view", "edit", "delete"];

  // Find the current page being edited
  const currentPage = pages.find((p) => p.p_id === Number(selectedPage));

  // Helper to get allowed actions per resource dynamically
  const getActionsForResource = () => STANDARD_ACTIONS;

  const checkRolePermission = (roleId, pageId, resId, action) => {
    // Check if there's a local override first
    const local = localChanges.find(
      (c) =>
        c.role_id === roleId &&
        c.page_id === pageId &&
        c.res_id === resId &&
        c.action === action
    );
    if (local) return local.enabled;

    // Otherwise check if it exists in saved permissions
    return permissions.some(
      (p) =>
        p.role_id === roleId &&
        p.page_id === pageId &&
        p.res_id === resId &&
        p.action === action
    );
  };

  const toggleAction = (roleId, pageId, resId, action) => {
    setLocalChanges((prev) => {
      const existingIndex = prev.findIndex(
        (c) =>
          c.role_id === roleId &&
          c.page_id === pageId &&
          c.res_id === resId &&
          c.action === action
      );

      if (existingIndex !== -1) {
        // Flip local state
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          enabled: !updated[existingIndex].enabled,
        };
        return updated;
      }

      // If not in local changes, base toggle on the current *computed* state
      const currentEnabled = checkRolePermission(roleId, pageId, resId, action);

      return [
        ...prev,
        {
          role_id: roleId,
          page_id: pageId,
          res_id: resId,
          action,
          enabled: !currentEnabled, // toggle it
        },
      ];
    });
  };

  const saveAll = async () => {
    if (!selectedRole || !selectedPage) {
      alert("Please select Role and Page before saving.");
      return;
    }

    try {
      setSaving(true);
      const changes = [...localChanges];
      for (const c of changes) {
        if (c.enabled) {
          await axios.post(`${BASE_URL}/api/permissions`, c, { headers });
        } else {
          const existing = permissions.find(
            (p) =>
              p.role_id === c.role_id &&
              p.page_id === c.page_id &&
              p.res_id === c.res_id &&
              p.action === c.action
          );
          if (existing) {
            await axios.delete(
              `${BASE_URL}/api/permissions/${existing.perm_id}`,
              { headers }
            );
          }
        }
      }

      await loadInitialData();
      setLocalChanges([]);
      setEditing(false);

      // ✅ 🔄 Notify all components (like AdminDashboard) to refresh
      window.dispatchEvent(new Event("permissionsUpdated"));

      alert("✅ Permissions saved successfully!");
    } catch (err) {
      console.error("❌ Save failed:", err);
      alert("Failed to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setLocalChanges([]);
  };

  // -------------------- UI --------------------

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3">Loading...</p>
      </div>
    );
  }

  // Filter resources if user picked one specifically
  const visibleResources =
    selectedResource && selectedResource !== ""
      ? resources.filter((r) => Number(r.res_id) === Number(selectedResource))
      : resources;

  return (
    <div className="form-wrapper my-3">
      <div className="container-fluid pb-4 permission-manager">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="fw-bold pb-2">🔒 Security Centre</h3>
        </div>

        {/* Filters */}
        <div className="card p-3 mb-4">
          <div className="row align-items-center g-3">
            <div className="col-md-3">
              <label className="form-label fw-bold">Role</label>
              <select
                className="form-select"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="">-- Select Role --</option>
                {roles.map((r) => (
                  <option key={r.r_id} value={r.r_id}>
                    {r.r_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label fw-bold">Page</label>
              <select
                className="form-select"
                value={selectedPage}
                onChange={(e) => {
                  setSelectedPage(e.target.value);
                  setSelectedResource(""); // reset resource on page change
                }}
              >
                <option value="">-- Select Page --</option>
                {pages.map((p) => (
                  <option key={p.p_id} value={p.p_id}>
                    {p.p_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label fw-bold">Resource</label>
              <select
                className="form-select"
                value={selectedResource}
                onChange={(e) => setSelectedResource(e.target.value)}
                disabled={!resources.length}
              >
                <option value="">-- All Resources --</option>
                {resources.map((r) => (
                  <option key={r.res_id} value={r.res_id}>
                    {r.res_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Permissions Table */}
        {selectedRole && selectedPage && (
          <div className="card p-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="fw-bold text-primary mb-0">
                {pages.find((p) => p.p_id === Number(selectedPage))?.p_name}
              </h6>

              {PERMISSIONS.permissions.edit &&
                (!editing ? (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => setEditing(true)}
                  >
                    Edit
                  </button>
                ) : (
                  <div>
                    <button
                      className="btn btn-sm btn-outline-secondary me-2"
                      onClick={cancelEdit}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={saveAll}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>
                ))}
            </div>

            {visibleResources.map((res) => {
              const activeActions = getActionsForResource(
                currentPage?.p_code || currentPage?.p_name,
                res.res_name
              ).filter((a) =>
                checkRolePermission(
                  Number(selectedRole),
                  Number(selectedPage),
                  res.res_id,
                  a
                )
              );

              return (
                <div key={res.res_id} className="permission-row mb-3">
                  <div className="fw-semibold">{res.res_name}</div>
                  {!editing ? (
                    <div className="text-muted">
                      {activeActions.length > 0
                        ? activeActions.join(", ")
                        : "No access"}
                    </div>
                  ) : (
                    <div className="d-flex flex-wrap gap-3 mt-2">
                      {getActionsForResource(
                        currentPage?.p_code || currentPage?.p_name,
                        res.res_name
                      ).map((a) => (
                        <div key={a} className="form-check form-switch">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={checkRolePermission(
                              Number(selectedRole),
                              Number(selectedPage),
                              res.res_id,
                              a
                            )}
                            onChange={() =>
                              toggleAction(
                                Number(selectedRole),
                                Number(selectedPage),
                                res.res_id,
                                a
                              )
                            }
                          />
                          <label className="form-check-label text-capitalize">
                            {a}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default PermissionManager;
